import { CXoneVoiceClient } from '@nice-devone/voice-sdk';
import { logger } from '../../../../utils/logger';
import { CXoneEventsSubscriptionContext, EventSubscription } from '../../subscription-context';
import {
  getWebRtcReadyState,
  isVoiceConnectionActive,
  tryConnectPendingAgentLeg,
} from '../../webrtc-connection';

/** Fast poll while waiting for SIP registration / AudioCodes connect. */
const JSSIP_POLL_FAST_MS = 100;
/** Slow poll after WebRTC is ready — still catches late pending agent legs. */
const JSSIP_POLL_SLOW_MS = 1500;

export function subscribeVoiceConnectionEvents(
  voiceClient: CXoneVoiceClient,
  ctx: CXoneEventsSubscriptionContext
): EventSubscription[] {
  const subscriptions: EventSubscription[] = [];

  if (voiceClient.onConnectionStatusChanged) {
    const voiceConnSub = voiceClient.onConnectionStatusChanged.subscribe(
      (status: { status?: string; isLoggedIn?: boolean }) => {
        const { webRtcReady } = getWebRtcReadyState(voiceClient);
        const isConnected = isVoiceConnectionActive(status, webRtcReady);

        logger.debug('[Events] Voice connection status changed:', {
          status,
          webRtcReady,
          isConnected,
          pendingLeg: ctx.pendingAgentLegRef.current,
        });

        ctx.setIsWebRtcConnected(isConnected);
        ctx.isWebRtcConnectedRef.current = isConnected;

        if (isConnected) {
          tryConnectPendingAgentLeg(voiceClient, ctx.pendingAgentLegRef, 'connection status');
        }
      }
    );
    subscriptions.push(voiceConnSub);
  }

  let pollIntervalMs = JSSIP_POLL_FAST_MS;
  let jssipPollInterval: ReturnType<typeof setInterval> | null = null;

  const runPollTick = () => {
    const { webRtcReady } = getWebRtcReadyState(voiceClient);

    if (webRtcReady && !ctx.isWebRtcConnectedRef.current) {
      logger.debug('[Events] JsSIP poll detected registration, updating state');
      ctx.isWebRtcConnectedRef.current = true;
      ctx.setIsWebRtcConnected(true);
    }

    if (webRtcReady) {
      tryConnectPendingAgentLeg(voiceClient, ctx.pendingAgentLegRef, 'JsSIP poll');

      if (pollIntervalMs !== JSSIP_POLL_SLOW_MS) {
        pollIntervalMs = JSSIP_POLL_SLOW_MS;
        if (jssipPollInterval) {
          clearInterval(jssipPollInterval);
        }
        jssipPollInterval = setInterval(runPollTick, pollIntervalMs);
        logger.debug('[Events] JsSIP poll slowed after WebRTC ready', {
          intervalMs: pollIntervalMs,
        });
      }
    }
  };

  jssipPollInterval = setInterval(runPollTick, pollIntervalMs);

  subscriptions.push({
    unsubscribe: () => {
      if (jssipPollInterval) {
        clearInterval(jssipPollInterval);
        jssipPollInterval = null;
      }
    },
  });

  return subscriptions;
}
